import { useState } from "react";
import { FaSearch, FaTimes, FaCalendarAlt, FaFilter, FaCheckCircle, FaClock } from "react-icons/fa";
import DynamicSelect from "./DynamicSelect";

const AppointmentFiltersAndSummary = ({
  filterStatus,
  setFilterStatus,
  selectedDoctor,
  setSelectedDoctor,
  patientSearch,
  setPatientSearch,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  appointments,
  doctorOptions,
  onReset,
  showPatientSearch = true,
  showDateFilters = true,
  totalLabel = "Total",
  statuses = ["all", "pending", "complete", "cancelled"],
  statusFilterStyles = {
    all: "bg-M-primary-color/90 text-white",
    pending: "bg-amber-600 text-white",
    complete: "bg-green-600 text-white",
    cancelled: "bg-red-600 text-white",
  },
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handleClearSearch = () => {
    setPatientSearch("");
  };

  return (
    <div>
      {/* Filters Section */}
      <div className="bg-white p-6 rounded-md shadow-md transition-all duration-300">
        <div className="flex justify-between items-center mb-4 md:mb-6">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FaFilter className="text-M-primary-color/90" />
            Filters
          </h3>
          <button
            className="md:hidden p-2 rounded-full bg-M-primary-color/90 text-white hover:bg-M-primary-color transition-colors"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            aria-label={isFilterOpen ? "Close filters" : "Open filters"}
          >
            {isFilterOpen ? <FaTimes /> : <FaFilter />}
          </button>
        </div>
        <div
          className={`${
            isFilterOpen ? "block" : "hidden"
          } md:block transition-all duration-300 ease-in-out`}
        >
          <div
            className={`grid grid-cols-1 gap-4 ${
              showPatientSearch && showDateFilters
                ? "md:grid-cols-3 lg:grid-cols-5"
                : "md:grid-cols-2"
            }`}
          >
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="flex flex-wrap gap-1">
                {statuses.map((status) => (
                  <button
                    key={status}
                    onClick={() => {
                      setFilterStatus(status);
                      // console.log("Filter status set to:", status);
                    }}
                    className={`relative px-3 py-2.5 rounded-lg text-xs font-medium transition-all duration-200 transform hover:scale-105 whitespace-nowrap ${
                      filterStatus === status
                        ? statusFilterStyles[status]
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                    aria-label={`Filter by ${status} status`}
                    title={status.charAt(0).toUpperCase() + status.slice(1)}
                  >
                    <span
                      className={`absolute inset-0 rounded-lg opacity-0 ${
                        filterStatus === status ? "animate-pulse bg-white/20" : ""
                      }`}
                    ></span>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Doctor Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Doctor
              </label>
              <DynamicSelect
                options={[{ value: "", label: "All Doctors" }, ...doctorOptions]}
                onChange={(e) => setSelectedDoctor(e?.value || "")}
                value={
                  selectedDoctor
                    ? {
                        value: selectedDoctor,
                        label:
                          doctorOptions.find((app) => app.value === selectedDoctor)
                            ?.label || "",
                      }
                    : ""
                }
                isSearchable={true}
                placeholder="Select a doctor"
                className="w-full rounded-md border-gray-300 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>

            {/* Patient Search */}
            {showPatientSearch && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient Search
                </label>
                <div className="relative">
                  <input
                    className="input-field relative pl-9"
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    placeholder="Name or Mobile"
                    aria-label="Search patients by name or mobile"
                  />
                  <FaSearch className="absolute top-3 left-3 text-gray-400" />
                  {patientSearch && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Start Date */}
            {showDateFilters && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    className="input-field relative pl-9"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    aria-label="Select start date"
                  />
                  <FaCalendarAlt className="absolute top-3 left-3 text-gray-400" />
                </div>
              </div>
            )}

            {/* End Date */}
            {showDateFilters && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    className="input-field relative pl-9"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    aria-label="Select end date"
                  />
                  <FaCalendarAlt className="absolute top-3 left-3 text-gray-400" />
                </div>
              </div>
            )}
          </div>

          {/* Reset Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onReset}
              className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              aria-label="Reset all filters"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Count Summary */}
      <div className="bg-white p-6 my-6 rounded-xl shadow-md border-l-4 border-M-primary-color/90">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Summary</h3>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-3">
            <span className="font-medium text-gray-700">{totalLabel}:</span>
            <span className="bg-teal-100 px-3 py-1 rounded-full text-teal-700 font-semibold">
              {appointments.length}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-medium text-gray-700">Complete:</span>
            <span className="bg-emerald-100 px-3 py-1 rounded-full text-emerald-700 font-semibold flex items-center gap-1">
              <FaCheckCircle className="text-emerald-600" />
              {appointments.filter((app) => app.status === "approved").length}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-medium text-gray-700">Pending:</span>
            <span className="bg-amber-100 px-3 py-1 rounded-full text-amber-700 font-semibold flex items-center gap-1">
              <FaClock className="text-amber-600" />
              {appointments.filter((app) => app.status === "pending").length}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-medium text-gray-700">Cancelled:</span>
            <span className="bg-rose-100 px-3 py-1 rounded-full text-rose-700 font-semibold flex items-center gap-1">
              <FaTimes className="text-rose-600" />
              {appointments.filter((app) => app.status === "cancelled").length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentFiltersAndSummary;